/**
 * Disk Health Page
 * ================
 * Unified view of all storage drives across all assets
 * (Proxmox hosts, Synology NAS, UniFi NVR).
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Activity,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  HardDrive,
  Server,
  Wifi,
  Thermometer,
  Database,
  CheckCircle,
} from 'lucide-react'
import { Card, CardContent } from '@/components/Card'
import Button from '@/components/Button'
import { useDiskHealth, useDiskHealthStats } from '@/hooks/useDiskHealth'
import { formatDriveCapacity } from '@/utils/storage'
import type { DiskHealthSearchParams, DiskHealthSortField, SortOrder } from '@/types'

// =============================================================================
// Constants
// =============================================================================

const HEALTH_OPTIONS = [
  { value: '', label: 'All Health' },
  { value: 'good', label: 'Good' },
  { value: 'warning', label: 'Warning' },
  { value: 'critical', label: 'Critical' },
  { value: 'failed', label: 'Failed' },
  { value: 'unknown', label: 'Unknown' },
]

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'ssd', label: 'SSD' },
  { value: 'hdd', label: 'HDD' },
  { value: 'nvme', label: 'NVMe' },
  { value: 'sas', label: 'SAS' },
]

const SOURCE_OPTIONS = [
  { value: '', label: 'All Sources' },
  { value: 'proxmox', label: 'Proxmox' },
  { value: 'synology', label: 'Synology' },
  { value: 'unifi', label: 'UniFi' },
]

/** Icon for each infrastructure source */
function SourceIcon({ source }: { source: string | null }) {
  switch (source) {
    case 'proxmox':
      return <span title="Proxmox"><Server size={14} className="text-orange-500 flex-shrink-0" /></span>
    case 'synology':
      return <span title="Synology"><HardDrive size={14} className="text-blue-500 flex-shrink-0" /></span>
    case 'unifi':
      return <span title="UniFi"><Wifi size={14} className="text-purple-500 flex-shrink-0" /></span>
    default:
      return <span title="Manual"><Database size={14} className="text-gray-400 flex-shrink-0" /></span>
  }
}

/** Health badge with color coding */
function HealthBadge({ health, badSectors }: { health: string; badSectors?: number | null }) {
  const colorClass =
    health === 'good'
      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      : health === 'warning'
      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      : health === 'critical' || health === 'failed'
      ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'

  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
      {health || 'Unknown'}
      {badSectors && badSectors > 0 ? ` (${badSectors} bad)` : ''}
    </span>
  )
}

/** Temperature with color coding */
function TempDisplay({ temp }: { temp: number | null }) {
  if (temp == null) return <span className="text-gray-400">-</span>

  const colorClass =
    temp >= 55
      ? 'text-red-600 dark:text-red-400'
      : temp >= 45
      ? 'text-yellow-600 dark:text-yellow-400'
      : ''

  return <span className={colorClass}>{temp}°C</span>
}

/** Power-on hours displayed as years */
function PowerOnDisplay({ hours }: { hours: number | null }) {
  if (hours == null) return <span className="text-gray-400">-</span>
  const years = Math.round((hours / 24 / 365) * 10) / 10
  return (
    <span title={`${hours.toLocaleString()} hours`}>
      {years}y
    </span>
  )
}

/** Wear level / remaining life display */
function LifeDisplay({ wearPercent }: { wearPercent: number | null }) {
  if (wearPercent == null) return <span className="text-gray-400">-</span>

  const remaining = 100 - wearPercent
  const colorClass =
    wearPercent >= 80
      ? 'text-red-600 dark:text-red-400'
      : wearPercent >= 50
      ? 'text-yellow-600 dark:text-yellow-400'
      : 'text-green-600 dark:text-green-400'

  return (
    <span className={colorClass} title={`${remaining}% life remaining`}>
      {remaining}%
    </span>
  )
}

// =============================================================================
// Stat Card Component
// =============================================================================

interface StatCardProps {
  title: string
  value: string | number
  icon: React.ReactNode
  detail?: string
  loading?: boolean
  color: string
}

function StatCard({ title, value, icon, detail, loading, color }: StatCardProps) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${color}`}>
          {icon}
        </div>
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
          <p className="text-2xl font-semibold text-gray-900 dark:text-white">
            {loading ? '...' : value}
          </p>
          {detail && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{detail}</p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

// =============================================================================
// Sort Header Component
// =============================================================================

interface SortHeaderProps {
  label: string
  field: DiskHealthSortField
  currentSort: DiskHealthSortField
  currentOrder: SortOrder
  onSort: (field: DiskHealthSortField) => void
  className?: string
}

function SortHeader({ label, field, currentSort, currentOrder, onSort, className = '' }: SortHeaderProps) {
  const isActive = currentSort === field
  return (
    <th
      className={`px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 select-none ${className}`}
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {isActive ? (
          currentOrder === 'asc' ? (
            <ChevronUp size={14} className="text-primary-500" />
          ) : (
            <ChevronDown size={14} className="text-primary-500" />
          )
        ) : (
          <ChevronUp size={14} className="opacity-0 group-hover:opacity-30" />
        )}
      </div>
    </th>
  )
}

// =============================================================================
// Main Page Component
// =============================================================================

export default function DiskHealthPage() {
  const navigate = useNavigate()
  const [searchInput, setSearchInput] = useState('')

  const [searchParams, setSearchParams] = useState<DiskHealthSearchParams>({
    page: 1,
    limit: 100,
    sort_by: 'health',
    sort_order: 'asc',
  })

  // Data fetching
  const { data, isLoading, error } = useDiskHealth(searchParams)
  const { data: statsData, isLoading: statsLoading } = useDiskHealthStats()

  const drives = data?.data ?? []
  const meta = data?.meta
  const stats = statsData?.data

  // Handlers
  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSearchParams((prev) => ({
      ...prev,
      search: searchInput || undefined,
      page: 1,
    }))
  }

  const handleFilterChange = (key: keyof DiskHealthSearchParams, value: string) => {
    setSearchParams((prev) => ({
      ...prev,
      [key]: value || undefined,
      page: 1,
    }))
  }

  const handleSort = (field: DiskHealthSortField) => {
    setSearchParams((prev) => ({
      ...prev,
      sort_by: field,
      sort_order: prev.sort_by === field && prev.sort_order === 'asc' ? 'desc' : 'asc',
      page: 1,
    }))
  }

  const handlePageChange = (newPage: number) => {
    setSearchParams((prev) => ({ ...prev, page: newPage }))
  }

  // Build health summary string
  const healthSummary = stats
    ? [
        stats.by_health.good && `${stats.by_health.good} good`,
        stats.by_health.warning && `${stats.by_health.warning} warning`,
        stats.by_health.critical && `${stats.by_health.critical} critical`,
        stats.by_health.failed && `${stats.by_health.failed} failed`,
      ]
        .filter(Boolean)
        .join(', ')
    : ''

  // Oldest drive in years
  const oldestYears = stats?.oldest_drive_hours
    ? Math.round((stats.oldest_drive_hours / 24 / 365) * 10) / 10
    : null

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">Error loading disk health data</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Activity className="text-green-500" size={28} />
          Disk Health
        </h1>
        <p className="text-gray-500 dark:text-gray-400">
          Storage drive health across all infrastructure
        </p>
      </div>

      {/* Summary stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Drives"
          value={stats?.total_drives ?? 0}
          icon={<HardDrive size={24} />}
          loading={statsLoading}
          color="bg-primary-50 text-primary-600 dark:bg-primary-900/50 dark:text-primary-400"
        />
        <StatCard
          title="Total Capacity"
          value={stats ? `${stats.total_capacity_tb} TB` : '0 TB'}
          icon={<Database size={24} />}
          loading={statsLoading}
          color="bg-blue-50 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400"
        />
        <StatCard
          title="Health Summary"
          value={
            stats
              ? `${stats.by_health.good ?? 0} / ${stats.total_drives}`
              : '0'
          }
          icon={<CheckCircle size={24} />}
          detail={healthSummary}
          loading={statsLoading}
          color={
            stats && (stats.by_health.critical || stats.by_health.failed)
              ? 'bg-red-50 text-red-600 dark:bg-red-900/50 dark:text-red-400'
              : stats && stats.by_health.warning
              ? 'bg-yellow-50 text-yellow-600 dark:bg-yellow-900/50 dark:text-yellow-400'
              : 'bg-green-50 text-green-600 dark:bg-green-900/50 dark:text-green-400'
          }
        />
        <StatCard
          title="Hottest / Oldest"
          value={
            stats?.hottest_drive_temp != null
              ? `${stats.hottest_drive_temp}°C`
              : '-'
          }
          icon={<Thermometer size={24} />}
          detail={oldestYears != null ? `Oldest drive: ${oldestYears}y` : undefined}
          loading={statsLoading}
          color={
            stats && stats.hottest_drive_temp != null && stats.hottest_drive_temp >= 55
              ? 'bg-red-50 text-red-600 dark:bg-red-900/50 dark:text-red-400'
              : stats && stats.hottest_drive_temp != null && stats.hottest_drive_temp >= 45
              ? 'bg-yellow-50 text-yellow-600 dark:bg-yellow-900/50 dark:text-yellow-400'
              : 'bg-orange-50 text-orange-600 dark:bg-orange-900/50 dark:text-orange-400'
          }
        />
      </div>

      {/* Filters */}
      <Card>
        <CardContent>
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={16}
                />
                <input
                  name="search"
                  type="text"
                  placeholder="Search by device, model, serial..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                />
              </div>
            </div>
            <select
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              value={searchParams.health ?? ''}
              onChange={(e) => handleFilterChange('health', e.target.value)}
            >
              {HEALTH_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <select
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              value={searchParams.drive_type ?? ''}
              onChange={(e) => handleFilterChange('drive_type', e.target.value)}
            >
              {TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <select
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              value={searchParams.source ?? ''}
              onChange={(e) => handleFilterChange('source', e.target.value)}
            >
              {SOURCE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <Button type="submit" variant="secondary">
              Search
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Drive table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b dark:border-gray-600">
              <tr>
                <SortHeader
                  label="Device"
                  field="device"
                  currentSort={searchParams.sort_by ?? 'health'}
                  currentOrder={searchParams.sort_order ?? 'asc'}
                  onSort={handleSort}
                  className="px-4"
                />
                <SortHeader
                  label="Type"
                  field="drive_type"
                  currentSort={searchParams.sort_by ?? 'health'}
                  currentOrder={searchParams.sort_order ?? 'asc'}
                  onSort={handleSort}
                />
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Model
                </th>
                <SortHeader
                  label="Size"
                  field="capacity_gb"
                  currentSort={searchParams.sort_by ?? 'health'}
                  currentOrder={searchParams.sort_order ?? 'asc'}
                  onSort={handleSort}
                />
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Serial
                </th>
                <SortHeader
                  label="Temp"
                  field="temperature_c"
                  currentSort={searchParams.sort_by ?? 'health'}
                  currentOrder={searchParams.sort_order ?? 'asc'}
                  onSort={handleSort}
                />
                <SortHeader
                  label="Power-On"
                  field="power_on_hours"
                  currentSort={searchParams.sort_by ?? 'health'}
                  currentOrder={searchParams.sort_order ?? 'asc'}
                  onSort={handleSort}
                />
                <SortHeader
                  label="Life"
                  field="wear_level_percent"
                  currentSort={searchParams.sort_by ?? 'health'}
                  currentOrder={searchParams.sort_order ?? 'asc'}
                  onSort={handleSort}
                />
                <SortHeader
                  label="Health"
                  field="health"
                  currentSort={searchParams.sort_by ?? 'health'}
                  currentOrder={searchParams.sort_order ?? 'asc'}
                  onSort={handleSort}
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : drives.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center">
                    <HardDrive className="mx-auto text-gray-300 dark:text-gray-600 mb-4" size={48} />
                    <p className="text-gray-500 dark:text-gray-400">No storage drives found</p>
                    {(searchParams.search || searchParams.health || searchParams.drive_type || searchParams.source) && (
                      <button
                        onClick={() => {
                          setSearchInput('')
                          setSearchParams({ page: 1, limit: 100, sort_by: 'health', sort_order: 'asc' })
                        }}
                        className="text-primary-600 dark:text-primary-400 hover:underline mt-2 inline-block"
                      >
                        Clear all filters
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                drives.map((drive) => (
                  <tr
                    key={drive.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                    onClick={() => navigate(`/assets/${drive.asset_asset_id}`)}
                  >
                    {/* Device (with source icon) */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <SourceIcon source={drive.infrastructure_source} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {drive.asset_name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {drive.slot || drive.name || '-'}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* Type */}
                    <td className="px-3 py-3 text-sm text-gray-900 dark:text-white">
                      {drive.drive_type?.toUpperCase() || '-'}
                    </td>

                    {/* Model */}
                    <td className="px-3 py-3 text-sm text-gray-900 dark:text-white max-w-[200px] truncate" title={drive.model || undefined}>
                      {drive.model || '-'}
                    </td>

                    {/* Size */}
                    <td className="px-3 py-3 text-sm text-gray-900 dark:text-white">
                      {formatDriveCapacity(drive.capacity_gb)}
                    </td>

                    {/* Serial */}
                    <td className="px-3 py-3 text-sm font-mono text-gray-500 dark:text-gray-400">
                      {drive.serial_number || '-'}
                    </td>

                    {/* Temp */}
                    <td className="px-3 py-3 text-sm text-gray-900 dark:text-white">
                      <TempDisplay temp={drive.temperature_c} />
                    </td>

                    {/* Power-On */}
                    <td className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                      <PowerOnDisplay hours={drive.power_on_hours} />
                    </td>

                    {/* Life */}
                    <td className="px-3 py-3 text-sm">
                      <LifeDisplay wearPercent={drive.wear_level_percent} />
                    </td>

                    {/* Health */}
                    <td className="px-3 py-3 text-sm">
                      <HealthBadge health={drive.health} badSectors={drive.bad_sectors} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {meta && meta.total_pages > 1 && (
          <div className="px-6 py-4 border-t dark:border-gray-700 flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing {(meta.page - 1) * meta.limit + 1} to{' '}
              {Math.min(meta.page * meta.limit, meta.total)} of {meta.total} drives
            </p>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={!meta.has_prev}
                onClick={() => handlePageChange(meta.page - 1)}
              >
                <ChevronLeft size={16} />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!meta.has_next}
                onClick={() => handlePageChange(meta.page + 1)}
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
